// /finance/profit — 利润表
// v1.31: 月度聚合 + 订单粒度
// 设计: 顶部月度趋势 (KPI 简化) + 列表 (订单 × 利润分布)
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { CardSkeleton } from '../components/Skeleton';
import { customerApi, financeApi, orderApi } from '../lib/api';
import { fmtMoney, fmtMoneyCompact, fmtPctFromValue } from '../lib/format';

export const Route = createFileRoute('/finance/profit')({
  component: ProfitList,
});

// v1.31: 导出 for unit test
export { ProfitList };

type Granularity = 'month' | 'quarter';

function ProfitList() {
  const [granularity, setGranularity] = useState<Granularity>('month');
  const summaryQ = useQuery({
    queryKey: ['finance', 'profit', 'summary', granularity],
    queryFn: () => financeApi.profitSummary(granularity),
    refetchInterval: 120000,
  });

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['orders', 'for-profit'],
    queryFn: () => orderApi.list(),
  });
  const { data: customers } = useQuery({
    queryKey: ['customers', 'lookup', 'profit'],
    queryFn: () => customerApi.list({ limit: 200 }),
  });
  const customerById = new Map((customers?.items ?? []).map((c) => [c.id, c]));

  const summaries = summaryQ.data?.items ?? [];
  const orders = (ordersData?.items ?? []).slice(0, 20); // 最近 20 单

  // 算 total stats
  const totalRevenue = summaries.reduce((acc, s) => acc + s.revenueCents, 0);
  const totalProfit = summaries.reduce((acc, s) => acc + s.profitCents, 0);
  const totalOrders = summaries.reduce((acc, s) => acc + s.orderCount, 0);
  const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="text-xs text-ink-3">
          {summaries.length} {granularity === 'month' ? '月' : '季'} · 共 {totalOrders} 订单
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-line rounded overflow-hidden">
            <button
              type="button"
              onClick={() => setGranularity('month')}
              className={`px-3 py-1 text-xs ${granularity === 'month' ? 'bg-accent text-white' : 'bg-surface text-ink-2 hover:bg-surface-2'}`}
            >
              月度
            </button>
            <button
              type="button"
              onClick={() => setGranularity('quarter')}
              className={`px-3 py-1 text-xs border-l border-line ${granularity === 'quarter' ? 'bg-accent text-white' : 'bg-surface text-ink-2 hover:bg-surface-2'}`}
            >
              季度
            </button>
          </div>
          <button
            type="button"
            onClick={() => summaryQ.refetch()}
            disabled={summaryQ.isFetching}
            className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-1"
          >
            <Icon
              name="refresh-cw"
              size="xs"
              className={summaryQ.isFetching ? 'animate-spin' : ''}
            />
          </button>
        </div>
      </div>

      {/* Total stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard
          label="总收入"
          value={fmtMoneyCompact(totalRevenue, summaries[0]?.currency ?? 'USD')}
          loading={summaryQ.isLoading}
        />
        <SummaryCard
          label="总利润"
          value={fmtMoneyCompact(totalProfit, summaries[0]?.currency ?? 'USD')}
          loading={summaryQ.isLoading}
          tone={totalProfit > 0 ? 'good' : 'bad'}
        />
        <SummaryCard
          label="平均利润率"
          value={fmtPctFromValue(avgMargin)}
          loading={summaryQ.isLoading}
          tone={avgMargin > 15 ? 'good' : avgMargin > 5 ? 'warn' : 'bad'}
        />
        <SummaryCard label="订单数" value={String(totalOrders)} loading={summaryQ.isLoading} />
      </div>

      {/* Monthly trend */}
      <div className="bg-surface border border-line rounded mb-5">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="text-sm font-semibold">{granularity === 'month' ? '月度' : '季度'}利润</h2>
        </div>
        {summaryQ.isLoading ? (
          <div className="p-4">
            <CardSkeleton />
          </div>
        ) : summaries.length === 0 ? (
          <div className="p-6 text-center text-ink-3 text-xs">暂无数据</div>
        ) : (
          <div className="divide-y divide-line">
            {summaries.slice(0, 12).map((s) => (
              <div key={s.month} className="px-4 py-3 flex items-center gap-3 hover:bg-surface-2">
                <div className="w-20 text-xs font-mono text-ink-2">{s.month}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-ink-3">收入</span>
                    <span className="font-medium">{fmtMoney(s.revenueCents, s.currency)}</span>
                    <span className="text-ink-3 ml-2">成本</span>
                    <span className="font-medium">{fmtMoney(s.costCents, s.currency)}</span>
                  </div>
                  <div className="mt-1 h-1 bg-surface-2 rounded overflow-hidden">
                    <ProfitBar revenue={s.revenueCents} profit={s.profitCents} cost={s.costCents} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div
                    className={`text-sm font-semibold ${
                      s.profitCents > 0 ? 'text-good' : 'text-bad'
                    }`}
                  >
                    {fmtMoney(s.profitCents, s.currency)}
                  </div>
                  <div className="text-[10px] text-ink-3">
                    {fmtPctFromValue(s.avgMargin)} · {s.orderCount} 单
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent orders — link to detail */}
      <div className="bg-surface border border-line rounded">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <h2 className="text-sm font-semibold">最近订单 (Top 20)</h2>
          <span className="text-[10px] text-ink-3">单订单利润见详情</span>
        </div>
        {ordersLoading ? (
          <div className="p-4">
            <CardSkeleton />
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            icon="bar-chart-3"
            title="还没有订单"
            description="订单出货后会按 v1.18 公式自动算利润"
          />
        ) : (
          <div className="divide-y divide-line">
            {orders.map((o) => {
              const customer = customerById.get(o.customerId);
              return (
                <div key={o.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {o.poNumber || o.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-ink-3 truncate">
                      {customer?.name ?? '—'} · {o.incoterm} · {o.status}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-medium">
                      {fmtMoney(o.totalAmountCents, o.currency)}
                    </div>
                    <div className="text-[10px] text-ink-3">合同金额</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-[10px] text-ink-3 text-center mt-4">
        公式: profit = revenue - cost - 海运 × 8% - 港杂 × 5% - 保险 × 0.3% (可在订单详情覆盖费率)
      </p>
    </div>
  );
}

function ProfitBar({
  revenue,
  profit,
  cost,
}: {
  revenue: number;
  profit: number;
  cost: number;
}) {
  if (revenue <= 0) return null;
  const costPct = (cost / revenue) * 100;
  const profitPct = (profit / revenue) * 100;
  return (
    <>
      <div
        className="h-full bg-warn/40 float-left"
        style={{ width: `${Math.min(100, costPct)}%` }}
        title={`成本 ${costPct.toFixed(0)}%`}
      />
      {profitPct > 0 && (
        <div
          className="h-full bg-good float-left"
          style={{ width: `${Math.min(100, profitPct)}%` }}
          title={`利润 ${profitPct.toFixed(0)}%`}
        />
      )}
    </>
  );
}

function SummaryCard({
  label,
  value,
  loading,
  tone = 'default',
}: {
  label: string;
  value: string;
  loading: boolean;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}) {
  const valueClass =
    tone === 'good'
      ? 'text-good'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'bad'
          ? 'text-bad'
          : '';
  return (
    <div className="bg-surface border border-line rounded p-3">
      <div className="text-[10px] text-ink-3 mb-1">{label}</div>
      {loading ? (
        <div className="h-6 w-20 bg-surface-2 rounded animate-pulse" />
      ) : (
        <div className={`text-base sm:text-lg font-semibold ${valueClass}`}>{value}</div>
      )}
    </div>
  );
}
